import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { STATUS_META, STATUSES } from "@/lib/constants";
import { api } from "@/lib/api";
import { TrendingUp } from "lucide-react";

const RANGES = [
  { days: 7, label: "7 gün" },
  { days: 30, label: "30 gün" },
  { days: 90, label: "90 gün" },
];

function formatDay(iso) {
  const [, m, d] = iso.split("-");
  return `${d}.${m}`;
}

export default function MomentumDialog({ open, onOpenChange }) {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .snapshots(days)
      .then((data) => {
        if (cancelled) return;
        setRows(
          data.map((d) => ({
            date: formatDay(d.date),
            raw: d.date,
            ...STATUSES.reduce(
              (acc, s) => ({ ...acc, [s]: d.counts?.[s] ?? 0 }),
              {}
            ),
            total: d.counts?.total ?? 0,
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setError("Veriler alınamadı.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, days]);

  const toggle = (s) => setHidden((h) => ({ ...h, [s]: !h[s] }));

  const first = rows[0];
  const last = rows[rows.length - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Zaman İçinde Değişim
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={days === r.days ? "default" : "outline"}
              className="h-8"
              onClick={() => setDays(r.days)}
              data-testid={`momentum-range-${r.days}`}
            >
              {r.label}
            </Button>
          ))}
        </div>

        {loading && (
          <div className="py-16 text-center text-sm text-neutral-500">
            Yükleniyor…
          </div>
        )}

        {!loading && error && (
          <div className="py-16 text-center text-sm text-red-600">{error}</div>
        )}

        {!loading && !error && rows.length < 2 && (
          <div className="py-12 text-center text-sm text-neutral-500 leading-relaxed">
            Grafik için en az iki günlük kayıt gerekiyor.
            <br />
            Kayıtlar sisteme her girildiğinde günde bir kez oluşuyor, birkaç gün
            sonra burası dolmaya başlayacak.
          </div>
        )}

        {!loading && !error && rows.length >= 2 && (
          <>
            <div className="h-[320px] w-full" data-testid="momentum-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={rows}
                  margin={{ top: 8, right: 12, bottom: 4, left: -20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickMargin={8}
                    minTickGap={16}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: "1px solid #E5E7EB",
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                    onClick={(e) => toggle(e.dataKey)}
                  />
                  {STATUSES.map((s) =>
                    hidden[s] ? null : (
                      <Line
                        key={s}
                        type="monotone"
                        dataKey={s}
                        name={STATUS_META[s].option || STATUS_META[s].short}
                        stroke={STATUS_META[s].dot}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                      />
                    )
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
              {STATUSES.map((s) => {
                const meta = STATUS_META[s];
                const diff = (last?.[s] ?? 0) - (first?.[s] ?? 0);
                const sign = diff > 0 ? "+" : "";
                return (
                  <div
                    key={s}
                    className={`${meta.bg} border ${meta.border} border-l-4 rounded-lg px-3 py-2`}
                  >
                    <div
                      className={`text-[10px] font-semibold leading-tight ${meta.text}`}
                    >
                      {meta.option || meta.short}
                    </div>
                    <div className="flex items-baseline gap-1.5 mt-0.5">
                      <span
                        className={`font-heading text-xl font-light ${meta.text}`}
                      >
                        {last?.[s] ?? 0}
                      </span>
                      <span
                        className={`text-[11px] font-mono ${
                          diff === 0
                            ? "text-neutral-400"
                            : diff > 0
                            ? "text-emerald-600"
                            : "text-red-600"
                        }`}
                      >
                        {sign}
                        {diff}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-[11px] text-neutral-500 leading-relaxed">
              Değişim rakamları seçili aralığın ilk ve son kaydı arasındaki farkı
              gösteriyor. Kimsenin sisteme girmediği günlerde kayıt oluşmaz, o
              günler grafikte atlanır.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
